import { faroCoreApiClient } from "@api/faro-core-api-client";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { IReactChildrenOnly } from "@custom-types/types";
import {
  CookieId,
  IntercomProvider,
  IntercomUserData,
  useIsCookieAccepted,
} from "@faro-lotv/foreign-observers";
import { runtimeConfig } from "@src/runtime-config";
import { useAppSelector } from "@store/store-helper";
import { loggedInUserSelector } from "@store/user/user-selector";
import { useEffect, useState } from "react";

/**
 * A loader that updates the Intercom-instance with the logged in user data. This is necessary to identify
 * users in Intercom and to distinguish them from (not logged in) visitors
 */
export function IntercomLoader({ children }: IReactChildrenOnly): JSX.Element {
  const isCookieAccepted = useIsCookieAccepted();
  const { handleErrorWithToast } = useErrorContext();
  const loggedInUser = useAppSelector(loggedInUserSelector);

  const [intercomInfo, setIntercomInfo] = useState<IntercomUserData>();

  // We need to load the user data from the API and pass it to Intercom
  useEffect(() => {
    async function updateIntercomUser(): Promise<void> {
      // Don't fetch Intercom data if user is not logged in
      if (!loggedInUser) {
        return;
      }

      try {
        const intercomInfo = await faroCoreApiClient.users.getIntercomHash();
        if (intercomInfo) {
          setIntercomInfo({
            userId: intercomInfo.userId,
            userHash: intercomInfo.hash,
          });
        }
      } catch (error) {
        handleErrorWithToast({
          id: "load intercom user data",
          title: "Failed to load Intercom.",
          error,
        });
      }
    }
    void updateIntercomUser();
  }, [handleErrorWithToast, loggedInUser]);

  return (
    <IntercomProvider
      appId={runtimeConfig.features.intercomAppId}
      user={intercomInfo}
      enabled={!!intercomInfo && isCookieAccepted(CookieId.intercom)}
    >
      {children}
    </IntercomProvider>
  );
}
